import { Alert, Check, Clock, ClockBack, Cross, Spinner } from '@factorialco/f0-react/icons/app'
import {
  IntegrationsSyncableState,
  IntegrationsSyncableStateStatusEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export type GroupedStatus =
  | 'syncing'
  | 'pending'
  | 'outdated'
  | 'partially_synced'
  | 'failed'
  | 'synced'

const isSyncing = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) =>
  statuses.has('syncing')

const isPending = (syncableStates: Array<IntegrationsSyncableState | undefined>) =>
  syncableStates.every((syncableState) => syncableState === undefined)

const isOutdated = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) => {
  return statuses.has('outdated') || statuses.has(undefined)
}

const hasFailed = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) =>
  statuses.has('failed') || statuses.has('invalid')

const isPartiallySynced = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) =>
  statuses.has('synced') && hasFailed(statuses)

export const groupedStatuses = (
  statusesGroups: Set<IntegrationsSyncableStateStatusEnum>[]
): GroupedStatus[] => {
  return statusesGroups.map((statuses) => {
    if (isSyncing(statuses)) return 'syncing'
    if (isOutdated(statuses)) return 'outdated'
    if (isPartiallySynced(statuses)) return 'partially_synced'
    if (hasFailed(statuses)) return 'failed'
    return 'synced'
  })
}

export const getBadgeGroupedBySyncables = (
  syncLoading: boolean,
  syncableStates: Array<IntegrationsSyncableState | undefined>
) => {
  const syncingBadge = {
    type: 'neutral',
    icon: Spinner,
    tooltip: i18n.t('integrations.sync_tooltips.syncing'),
  }

  const pendingBadge = {
    type: 'neutral',
    icon: Clock,
    tooltip: i18n.t('integrations.sync_tooltips.pending'),
  }

  const outdatedBadge = {
    type: 'neutral',
    icon: ClockBack,
    tooltip: i18n.t('integrations.sync_tooltips.outdated'),
  }

  const partiallySyncedBadge = {
    type: 'warning',
    icon: Alert,
    tooltip: i18n.t('integrations.sync_tooltips.partially_synced'),
  }

  const failedBadge = {
    type: 'critical',
    icon: Cross,
    tooltip: i18n.t('integrations.sync_tooltips.failed'),
  }

  const syncedBadge = {
    type: 'positive',
    icon: Check,
    tooltip: i18n.t('integrations.sync_tooltips.synced'),
  }

  if (syncLoading) return syncingBadge
  // The order of the status checks is by priority
  const statuses = new Set(syncableStates.map((syncableState) => syncableState?.status))

  if (isSyncing(statuses)) return syncingBadge
  if (isPending(syncableStates)) return pendingBadge
  if (isOutdated(statuses)) return outdatedBadge
  if (isPartiallySynced(statuses)) return partiallySyncedBadge
  if (hasFailed(statuses)) return failedBadge

  return syncedBadge
}
